import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Users, Award, Globe, Clock } from 'lucide-react';
import AnimatedCounter from './AnimatedCounter';

const StatsSection = styled.section`
  background: linear-gradient(135deg, #0a0e27 0%, #1e1b4b 40%, #581c87 100%);
  padding: 80px 0;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 0.5px, transparent 0);
    background-size: 30px 30px;
    opacity: 0.5;
  }
`;

const StatsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 2;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 40px;
  margin-top: 60px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 30px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 25px;
  }
`;

const StatCard = styled(motion.div)`
  text-align: center;
  background: rgba(135,206,235,0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(135,206,235,0.3);
  padding: 40px 30px;
  border-radius: 20px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    border-color: rgba(135,206,235,0.5);
    box-shadow: 0 10px 30px rgba(0,0,0,0.2);
  }
`;

const StatIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #87ceeb 0%, #1e3a8a 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: white;
`;

const StatNumber = styled.div`
  font-size: 3rem;
  font-weight: 900;
  color: #87ceeb;
  margin-bottom: 10px;
  font-family: 'Inter', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const StatLabel = styled.div`
  font-size: 1.1rem;
  color: #ffffff;
  font-weight: 600;
  margin-bottom: 10px;
`;

const StatDescription = styled.div`
  font-size: 0.9rem;
  color: rgba(255,255,255,0.8);
  line-height: 1.4;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 20px;
  background: linear-gradient(45deg, #00ff41, #00ffff, #ff0080, #ffff00);
  background-size: 300% 300%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 700;
  letter-spacing: -0.02em;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, #00ff41, #00ffff, #ff0080);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const SectionSubtitle = styled.p`
  text-align: center;
  font-size: 1.2rem;
  color: #ffffff;
  margin-bottom: 60px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 40px;
  }
`;

const CompanyStats = () => {
  const stats = [
    {
      icon: <Users size={40} />,
      number: 30,
      suffix: '+',
      label: 'Happy Clients',
      description: 'Businesses transformed with our solutions'
    },
    {
      icon: <Award size={40} />,
      number: 61,
      suffix: '+',
      label: 'Projects Completed',
      description: 'Successful deliveries across industries'
    },
    {
      icon: <Globe size={40} />,
      number: 8,
      suffix: '+',
      label: 'Regions Served',
      description: 'Global reach and international presence'
    },
    {
      icon: <Clock size={40} />,
      number: 4,
      suffix: '+',
      label: 'Years Experience',
      description: 'Proven track record in technology'
    }
  ];

  return (
    <StatsSection>
      <StatsContainer>
        <SectionTitle>Our Impact in Numbers</SectionTitle>
        <SectionSubtitle>
          Trusted by businesses worldwide to deliver exceptional technology solutions
        </SectionSubtitle>
        
        <StatsGrid>
          {stats.map((stat, index) => (
            <StatCard
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <StatIcon>
                {stat.icon}
              </StatIcon>
              <StatNumber>
                <AnimatedCounter end={stat.number} duration={2} />
                {stat.suffix}
              </StatNumber>
              <StatLabel>{stat.label}</StatLabel>
              <StatDescription>{stat.description}</StatDescription>
            </StatCard>
          ))}
        </StatsGrid>
      </StatsContainer>
    </StatsSection>
  );
};

export default CompanyStats;